import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import {
  apartmentPaymentsSummary,
  fetchApartmentPayments,
  paymentsCsvUrl,
  recordOfflinePayment,
  resendApartmentPaymentLink
} from 'api/apartment'
import { Loading } from 'app/components/generic/LottieComponents'
import Table from 'app/components/generic/Table'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'
import { formatAmount } from 'app/utils'

import ApartmentPageHeader from '../shared/ApartmentPageHeader'
import { useSelectedApartment } from '../shared/apartmentStore'
import MonthFilter from '../shared/MonthFilter'
import { PaymentLinkPanel } from '../shared/PaymentLinkCopy'

const useStyles = makeStyles((theme) => ({
  // No padding box around the whole page — the header/cards carry their own
  // horizontal padding, and the table runs edge-to-edge with its own horizontal
  // scroll (matches the main app's list pages).
  root: { minWidth: 0, overflowX: 'hidden' },
  header: { padding: theme.spacing(3, 3, 0) },
  summary: { marginBottom: theme.spacing(2) },
  summaryCard: { padding: theme.spacing(2) },
  summaryValue: { fontWeight: 600, marginTop: theme.spacing(0.5) }
}))

const STATUS_COLOR = {
  Paid: { color: '#2e7d32', background: '#e8f5e9' },
  Pending: { color: '#ef6c00', background: '#fff3e0' },
  Expired: { color: '#c62828', background: '#ffebee' },
  Partial: { color: '#1565c0', background: '#e3f2fd' },
  Waived: { color: '#616161', background: '#eeeeee' }
}

const StatusChip = ({ status }) => {
  const style = STATUS_COLOR[status] || {}
  return (
    <Chip
      label={status}
      size="small"
      style={{ color: style.color, backgroundColor: style.background }}
    />
  )
}

const SummaryCard = ({ label, value, classes }) => (
  <Grid item xs={6} md={3}>
    <Paper variant="outlined" className={classes.summaryCard}>
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="h6" className={classes.summaryValue}>
        {value}
      </Typography>
    </Paper>
  </Grid>
)

const tableOptions = {
  selectableRows: 'none',
  filterType: 'dropdown',
  // Match the main system: the grid is end-to-end with ONE horizontal
  // scroller of its own and a fixed header. Do not wrap it in another
  // overflow container or you get two scrollbars.
  responsive: 'vertical',
  fixedHeader: true,
  tableBodyMaxHeight: 'calc(100vh - 320px)',
  print: false,
  download: true,
  viewColumns: false,
  rowsPerPage: 25,
  rowsPerPageOptions: [10, 15, 25, 100],
  elevation: 0
}

const ApartmentPayments = () => {
  const classes = useStyles()
  const [apartment] = useSelectedApartment()
  const [rows, setRows] = React.useState(null)
  // '' = every month. The summary cards are derived from `rows`, so filtering
  // the fetch is what makes the cards report the selected month rather than
  // all time.
  const [month, setMonth] = React.useState('')
  // The freshly-minted renewal link, held so it can be copied rather than
  // vanishing with a toast. See PaymentLinkCopy for why it is always shown.
  const [renewedLink, setRenewedLink] = React.useState(null)

  React.useEffect(() => {
    let active = true
    setRows(null)
    fetchApartmentPayments(apartment.id, month).then(
      (res) => active && setRows(res)
    )
    return () => {
      active = false
    }
  }, [apartment.id, month])

  const monthFilter = (
    <MonthFilter
      value={month}
      onChange={setMonth}
      allowAll
      apartmentId={apartment.id}
    />
  )

  if (!rows) return <Loading />

  const summary = apartmentPaymentsSummary(rows)

  // Razorpay-driven, so read-only — except resending an unpaid/expired link
  // (manual resend, per the payments decision).
  // Keeps the month filter: refetching without it would silently snap the table
  // (and the cards) back to all-time the moment a coordinator records a payment.
  const refresh = () =>
    fetchApartmentPayments(apartment.id, month).then((res) => setRows(res))

  const resend = async (dataIndex) => {
    const row = rows[dataIndex]
    try {
      const link = await resendApartmentPaymentLink(row.uuid)
      // A resend always mints a fresh Razorpay link; whether the parent was
      // actually messaged is a separate question, and only the backend knows.
      // `!== false` on purpose — an older API omits the field, and treating
      // that as a failure would flag every successful resend. See the same
      // guard in Apartment/Enrolments.
      if (link.whatsapp_sent !== false) {
        eventAlert(
          `Link re-sent to ${row.parentName} on WhatsApp`,
          constants.SUCCESS
        )
      } else {
        eventAlert(
          `A fresh link was created for ${row.parentName}, but the WhatsApp ` +
            `did NOT go out. Copy it from the dialog and send it yourself.`,
          constants.ERROR,
          'Link renewed — WhatsApp not sent'
        )
      }
      // Always surfaced, not just on a reported failure: the renewal template
      // is Marketing-category, so Meta can withhold it while still reporting
      // the send as accepted.
      setRenewedLink({
        url: link.razorpay_short_url,
        amountLabel: formatAmount(link.net),
        whatsappSent: link.whatsapp_sent,
        parentName: row.parentName
      })
      refresh()
    } catch (error) {
      // request.js already surfaces a toast.
    }
  }

  // Coordinator records a UPI / bank-transfer payment (PDR §5.3).
  const recordOffline = async (dataIndex) => {
    const row = rows[dataIndex]
    // eslint-disable-next-line no-alert
    const mode = window.prompt('Payment mode? (UPI / Bank Transfer)', 'UPI')
    if (!mode) return
    // eslint-disable-next-line no-alert
    const reference = window.prompt('Reference / txn id (optional)', '') || ''
    try {
      await recordOfflinePayment(row.uuid, { mode, reference })
      eventAlert(
        `Offline payment recorded for ${row.parentName}`,
        constants.SUCCESS
      )
      refresh()
    } catch (error) {
      // request.js already surfaces a toast.
    }
  }

  const columns = [
    { name: 'parentName', label: 'Parent' },
    { name: 'children', label: 'Children' },
    { name: 'categories', label: 'Categories' },
    { name: 'month', label: 'Month' },
    {
      name: 'amount',
      label: 'Amount',
      options: { customBodyRender: (v) => formatAmount(v), filter: false }
    },
    {
      name: 'gst',
      label: 'incl. GST',
      options: { customBodyRender: (v) => formatAmount(v), filter: false }
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        customBodyRender: (v) => <StatusChip status={v} />
      }
    },
    { name: 'paymentMode', label: 'Mode', options: { filter: true } },
    { name: 'paymentId', label: 'Payment ID', options: { filter: false } },
    {
      // The Razorpay link itself, as the main system's Orders page shows it.
      // Historical links imported from the sheets carry no URL — only a payment
      // id — so they render a dash; every link raised by a booking has one.
      name: 'paymentUrl',
      label: 'Payment Link',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (v) =>
          v ? (
            <a href={v} target="_blank" rel="noopener noreferrer">
              {v}
            </a>
          ) : (
            '—'
          )
      }
    },
    { name: 'date', label: 'Date', options: { filter: false } },
    {
      name: '',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        download: false,
        customBodyRenderLite: (dataIndex) => {
          const row = rows[dataIndex]
          const status = row?.status
          const canAct = status === 'Expired' || status === 'Pending'
          if (!canAct && !row?.paymentUrl) return null
          return (
            <>
              {row?.paymentUrl && (
                <Button
                  size="small"
                  href={row.paymentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Link
                </Button>
              )}
              {canAct && (
                <>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => resend(dataIndex)}
                  >
                    Resend
                  </Button>
                  <Button size="small" onClick={() => recordOffline(dataIndex)}>
                    Offline
                  </Button>
                </>
              )}
            </>
          )
        }
      }
    }
  ]

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <ApartmentPageHeader
          title="Payments"
          action={
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {monthFilter}
              <Button
                variant="outlined"
                size="small"
                href={paymentsCsvUrl(apartment.id, month)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Export CSV
              </Button>
            </div>
          }
        />

        <Grid container spacing={2} className={classes.summary}>
          <SummaryCard
            label="Collected"
            value={formatAmount(summary.collected)}
            classes={classes}
          />
          <SummaryCard
            label="Pending"
            value={formatAmount(summary.pending)}
            classes={classes}
          />
          <SummaryCard
            label="Expired links"
            value={summary.expiredCount}
            classes={classes}
          />
          <SummaryCard
            label="Total links"
            value={summary.total}
            classes={classes}
          />
        </Grid>
      </div>

      <Table
        data={rows}
        columns={columns}
        options={tableOptions}
        disableFullHeight
      />

      <Dialog
        open={Boolean(renewedLink)}
        onClose={() => setRenewedLink(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {renewedLink
            ? `Link renewed — ${renewedLink.parentName}`
            : 'Link renewed'}
        </DialogTitle>
        <DialogContent>
          <PaymentLinkPanel
            url={renewedLink?.url}
            amountLabel={renewedLink?.amountLabel}
            whatsappSent={renewedLink?.whatsappSent}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewedLink(null)}>Done</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default ApartmentPayments
